<template>
  <basic-dialog v-model="dialog" :actionButtons="actionButtons" :loading="saving"
                :title="$t('tenants.duns_search')">
    <v-card-text class="scroll-content">
      <template v-if="identities && identities.length > 0">
        <div class="text-h6 mt-3 mb-3">
          <v-app-bar-nav-icon icon @click="backToSearch">
            <v-icon>arrow_back</v-icon>
          </v-app-bar-nav-icon>
          {{ $t('tenants.duns_search_results') }}
        </div>
        <div class="text-body-2 mt-3 mb-3">
          {{ $t('tenants.duns_search_results_help') }}:
        </div>

        <v-list three-line>
          <v-list-item-group v-model="selected" active-class="info--text">
            <template v-for="(identity,index) in identities">
              <v-list-item :key="identity.organization.duns" :value="identity" link>
                <v-list-item-content>
                  <v-list-item-title>{{ identity.organization.primaryName }}</v-list-item-title>
                  <v-list-item-subtitle
                    v-if="identity && identity.organization && identity.organization.primaryAddress">
                    <strong class="d-inline-block" style="width: 170px;">{{
                        $t('party.address_info')
                      }}: &nbsp;</strong>

                    <span>{{
                        organizationAddress(identity)
                      }}
                    </span>
                  </v-list-item-subtitle>
                  <v-list-item-subtitle
                    v-if="identity && identity.organization && identity.organization.registrationNumbers && identity.organization.registrationNumbers.length > 0">
                    <strong class="d-inline-block" style="width: 170px;">{{
                        $t('tenants.registration_number')
                      }}:</strong>
                    <span>{{ identity.organization.registrationNumbers[0].registrationNumber }}</span>
                  </v-list-item-subtitle>
                  <v-list-item-subtitle><strong class="d-inline-block"
                                                style="width: 170px;">{{ $t('tenants.details.duns_number') }}:</strong>
                    <span>{{
                        identity.organization.duns
                      }}</span></v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              <v-divider
                v-if="index < identities.length - 1"
                :key="identity.displaySequence"
              ></v-divider>
            </template>
          </v-list-item-group>
        </v-list>
      </template>

      <v-form v-else ref="form">
        <div :class="$vuetify.breakpoint.xsOnly ? 'caption' : ''" class="mt-4 mb-6">
          {{ $t('required_fields_are_marked_with_asterisk') }} (<span class="red--text">*</span>).
        </div>

        <v-text-field
          v-model="currentTenant.name"
          :disabled="true"
          :label="$t('tenants.organization_name')"
          :rules="[$rules.required]"
          autocomplete="organization"
          class="mt-5 pt-4 organization_name required"
          color="primary"
          outlined
          type="text"
          validate-on-blur
        ></v-text-field>
        <v-text-field
          v-model="currentTenant.registrationNumber"
          :label="$t('tenants.registration_number')"
          autocomplete="organization_registration_number"
          class="organization_registration_number"
          color="primary"
          outlined
          type="text"
          validate-on-blur
        ></v-text-field>
        <country-select v-model="currentTenant.country" :rules="[$rules.required]"
                        required></country-select>


        <template v-if="false">
          <v-text-field
            v-model="currentTenant.streetAddress"
            :label="$t('tenants.details.street_address')"
            :rules="[$rules.required]"
            autocomplete="streetAddress"
            class="mt-2 organization_street_address required"
            color="primary"
            outlined
            type="text"
            validate-on-blur
          ></v-text-field>
          <v-text-field
            v-model="currentTenant.zipCode"
            :label="$t('tenants.details.zip_code')"
            :rules="[$rules.required]"
            autocomplete="zipCode"
            class="mt-2 organization_zip_code required"
            color="primary"
            outlined
            type="text"
            validate-on-blur
          ></v-text-field>
          <v-text-field
            v-model="currentTenant.city"
            :label="$t('tenants.details.city')"
            :rules="[$rules.required]"
            autocomplete="city"
            class="mt-2 organization_city required"
            color="primary"
            outlined
            type="text"
            validate-on-blur
          ></v-text-field>

          <v-text-field
            v-model="currentTenant.dunsNumber"
            :label="$t('tenants.details.duns_number')"
            autocomplete="duns"
            class="mt-2 organization_duns"
            color="primary"
            outlined
            type="text"
            validate-on-blur
          ></v-text-field>
        </template>

      </v-form>

      <v-alert :value="showAlert" class="mt-4" outlined type="error">
        {{ $t('check_all_required_fields') }}
      </v-alert>
    </v-card-text>
  </basic-dialog>
</template>

<script>
import BasicDialog from "@/components/BasicDialog";
import CountrySelect from "@/components/CountrySelect";
import identityApi from "@/api/dnbidentity";

export default {
  name: 'DunsSearchDialog',
  components: {CountrySelect, BasicDialog},
  data() {
    return {
      dialog: false,
      saving: false,
      organization: '',
      showAlert: false,
      currentTenant: {},
      loadingSearch: false,
      identities: [],
      selected: null,
    };
  },
  computed: {
    actionButtons() {
      const buttons = {
        cancel: {
          click: this.cancel,
        },
        search: {
          click: this.search,
          loading: this.loadingSearch,
        }
      }
      if ((this.selected && this.identities?.length > 0) || this.identities?.length > 0) {
        delete buttons.search
        buttons.save = {
          click: this.save,
          disabled: !this.selected,
        }
      }
      return buttons;
    }
  },
  methods: {
    organizationAddress(identity) {
      return [
        identity?.organization?.primaryAddress?.streetAddress.line1,
        identity?.organization?.primaryAddress?.streetAddress.line2,
        [identity?.organization?.primaryAddress?.addressRegion?.name || identity?.organization?.primaryAddress?.addressRegion?.abbreviatedName,
          identity?.organization?.primaryAddress?.postalCode,
          identity?.organization?.primaryAddress?.addressLocality?.name].filter(Boolean).join(' '),

        identity?.organization?.primaryAddress?.addressCountry?.name].filter(Boolean).join(', ')
    },
    backToSearch() {
      this.selected = null;
      this.identities = [];
    },
    cancel() {
      this.dialog = false;
    },
    async search() {
      if (!this.$refs.form.validate()) return;
      this.loadingSearch = true;
      try {
        const identities = await identityApi.getDnBIdentity({
          name: this.currentTenant.name,
          registrationNumber: this.currentTenant.registrationNumber,
          countryISOAlpha2Code: this.currentTenant.country,
        })
        /*if (identities && identities.error && identities.error.errorCode === '20505') {
          this.$showErrorNotification(this.$t('tenants.duns_no_search_results'))
          this.loadingSearch = false;
          return;
        }*/
        if (identities && identities.length === 0) {
          this.$showErrorNotification(this.$t('tenants.duns_no_search_results'))
          this.loadingSearch = false;
          return;
        }
        this.identities = identities;
        if (identities && identities.length > 0) {
          this.selected = identities[0];
        }
      } catch (e) {
        this.$handleApiError(e, this.$t('tenants.duns_search_error'));
      }
      this.loadingSearch = false;
    },
    open(tenant) {
      this.dialog = true;
      this.currentTenant = Object.assign({}, tenant);
    },
    save() {
      this.$emit('save', this.selected)
      this.dialog = false;
    }
  },
  watch: {
    dialog(value) {
      if (!value) {
        this.currentTenant = {};
        this.showAlert = false;
        this.$refs.form?.reset();
        this.$emit('on-dismiss');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.scroll-content {
  max-height: calc(100vh - 240px);
  overflow: auto;
}

@media screen and (min-width: 600px) {
  .scroll-content {
    max-height: calc(100vh - 300px);
    overflow: auto;
  }
}
</style>
